import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import paymentService from '../services/paymentService';
import { bookingApi, customerApi, scheduleApi, seatApi } from '../services/api';
import Swal from 'sweetalert2';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState('processing'); // processing, success, error
  const [paymentResult, setPaymentResult] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    handlePaymentCallback();
  }, []);

  const handlePaymentCallback = async () => {
    try {
      // Check for payment result from location.state (embedded checkout callback)
      const locationState = location.state;
      let response = null;
      
      if (locationState && locationState.paymentResult) {
        // Payment result from embedded checkout callback
        response = {
          success: locationState.paymentResult.success,
          orderId: locationState.paymentResult.orderId,
          transactionId: locationState.paymentResult.transactionId,
          resultIndicator: locationState.paymentResult.resultIndicator
        };
        console.log('Payment result from location.state:', response);
      } else {
        // Parse payment response from URL (hosted checkout redirect)
        response = paymentService.parsePaymentResponse(window.location.search);
        console.log('Payment result from URL:', response);
      }
      
      setPaymentResult(response);

      // Get stored booking data from sessionStorage
      const storedData = paymentService.getStoredBookingData();
      
      // Also try to get from sessionStorage directly
      const fullBookingDataStr = sessionStorage.getItem('pendingBookingData');
      
      if (!storedData && !fullBookingDataStr) {
        console.error('No booking data found in sessionStorage');
        throw new Error('Booking data not found. Please try booking again.');
      }

      // Check if payment was successful
      // For embedded checkout, success comes via callback (location.state)
      // For hosted checkout, success comes via URL parameters
      const isPaymentSuccess = response.success || 
                               (locationState && locationState.paymentResult && locationState.paymentResult.success) ||
                               window.location.search.includes('result=SUCCESS') ||
                               window.location.search.includes('status=success') ||
                               window.location.search.includes('resultIndicator') ||
                               response.resultIndicator !== null;

      console.log('Payment success check:', isPaymentSuccess);

      if (isPaymentSuccess) {
        // Payment successful - complete the booking
        await completeBooking(storedData || {}, response);
      } else {
        // Payment failed
        setPaymentStatus('error');
        setProcessing(false);
        
        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: response.message || 'Payment could not be processed. Please try again.',
          confirmButtonColor: '#dc2626'
        }).then(() => {
          // Redirect back to booking page
          navigate('/book');
        });
      }
    } catch (error) {
      console.error('Payment callback error:', error);
      setPaymentStatus('error');
      setProcessing(false);
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'An error occurred while processing payment. Please try again.',
        confirmButtonColor: '#dc2626'
      }).then(() => {
        navigate('/book');
      });
    }
  };

  const completeBooking = async (storedData, paymentResponse) => {
    try {
      // Get the full booking data from sessionStorage
      const fullBookingData = JSON.parse(sessionStorage.getItem('pendingBookingData') || '{}');
      
      if (!fullBookingData.passengerDetails || !fullBookingData.journeyDetails) {
        throw new Error('Booking data incomplete. Please try booking again.');
      }

      const { passengerDetails, journeyDetails, selectedSeats, selectedDate, basePrice, totalAmount } = fullBookingData;
      // Get service charge from journey details for fallback calculation
      const serviceCharge = journeyDetails?.serviceCharge || 0;


      // Validate required data
      if (!passengerDetails || !passengerDetails.name || !passengerDetails.phone || !passengerDetails.gender) {
        throw new Error('Missing required passenger information. Please try booking again.');
      }

      if (!journeyDetails || !journeyDetails.scheduleDateId) {
        throw new Error('Missing journey information. Please try booking again.');
      }

      if (!selectedSeats || selectedSeats.length === 0) {
        throw new Error('No seats selected. Please try booking again.');
      }

      // Show processing message
      Swal.fire({
        title: 'Processing Booking...',
        text: 'Please wait while we confirm your booking.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Step 1: Find or create customer
      const customerData = {
        CustomerName: passengerDetails.name,
        Phone: passengerDetails.phone,
        Email: passengerDetails.email && passengerDetails.email.trim() !== '' ? passengerDetails.email.trim() : null,
        Gender: passengerDetails.gender,
        NIC: passengerDetails.nic && passengerDetails.nic.trim() !== '' ? passengerDetails.nic.trim() : null
      };

      const customerResponse = await customerApi.findOrCreateCustomer(customerData);
      let customer = customerResponse?.value || customerResponse?.data || customerResponse;
      let customerId = customer?.customerID || customer?.CustomerID;

      if (!customerId) {
        throw new Error('Failed to create or find customer');
      }

      // Step 2: Get vehicle ID
      let vehicleId = null;
      if (journeyDetails.scheduleId) {
        const scheduleResponse = await scheduleApi.getSchedule(journeyDetails.scheduleId);
        const schedule = scheduleResponse?.value || scheduleResponse;
        vehicleId = schedule?.busID || schedule?.vehicleId || schedule?.vehicle?.id;
      }

      if (!vehicleId) {
        throw new Error('Could not determine vehicle ID for booking');
      }

      // Step 3: Use existing booking ID (booking was created before payment with PaymentStatus="Pending")
      // We just need to update the payment status to "Completed"
      let firstBookingId = fullBookingData.bookingId || fullBookingData.bookingIds?.[0] || null;

      if (!firstBookingId) {
        throw new Error('Booking ID not found. Please try booking again.');
      }


      // Update payment status to "Completed" (non-blocking - don't fail if this errors)
      let paymentStatusUpdated = false;
      try {
        await bookingApi.updatePaymentStatus(firstBookingId, "Completed");
        paymentStatusUpdated = true;
      } catch (paymentStatusError) {
        console.error('Error updating payment status:', paymentStatusError);
        // Don't throw error - payment was successful, we'll show success message anyway
        // The booking exists and payment was processed, so we continue with success flow
      }

      // Clear stored data
      paymentService.clearStoredBookingData();
      sessionStorage.removeItem('pendingBookingData');

      // Show success
      setPaymentStatus('success');
      setProcessing(false);
      Swal.close(); // Close loading dialog

      // Format route information
      // Format route information - use journeyDetails.route which is already formatted
      const routeInfo = journeyDetails.route || 'N/A';
      
      // Format travel date
      const formattedDate = new Date(selectedDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Format departure time
      const departureTime = journeyDetails.departure || 'N/A';

      const result = await Swal.fire({
        icon: 'success',
        title: 'Payment Successful!',
        html: `
          <div class="text-left">
            <p class="mb-3 text-center font-semibold text-lg">Your booking has been confirmed!</p>
            
            <div class="bg-gray-50 p-4 rounded-lg mb-3">
              <p class="mb-2"><strong>Booking Reference:</strong> <span class="text-blue-600 font-semibold">BK${firstBookingId.toString().padStart(6, '0')}</span></p>
              <p class="mb-2"><strong>Payment Status:</strong> <span class="text-green-600 font-semibold">Completed</span></p>
              ${paymentResponse.transactionId || paymentResponse.orderId ? `<p class="mb-2"><strong>Transaction ID:</strong> ${paymentResponse.transactionId || paymentResponse.orderId}</p>` : ''}
            </div>
            
            <div class="border-t pt-3 mb-3">
              <p class="mb-1"><strong>Route:</strong> ${routeInfo}</p>
              <p class="mb-1"><strong>Travel Date:</strong> ${formattedDate}</p>
              <p class="mb-1"><strong>Departure Time:</strong> ${departureTime}</p>
              <p class="mb-1"><strong>Seats Booked:</strong> <span class="font-semibold">${selectedSeats.join(', ')}</span> (${selectedSeats.length} seat${selectedSeats.length > 1 ? 's' : ''})</p>
              <p class="mb-1"><strong>Total Amount:</strong> <span class="font-semibold text-green-600">Rs ${(totalAmount || ((basePrice * selectedSeats.length) + (serviceCharge * selectedSeats.length))).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
            </div>
            
            <p class="text-sm text-gray-600 text-center mt-3">Confirmation SMS will be sent shortly.</p>
          </div>
        `,
        confirmButtonColor: '#2563eb',
        confirmButtonText: 'Download Receipt',
        showCancelButton: true,
        cancelButtonText: 'View My Bookings',
        cancelButtonColor: '#6b7280',
        width: '500px'
      });

      // Download receipt if requested
      if (result.isConfirmed && firstBookingId) {
        try {
          Swal.fire({
            title: 'Downloading Receipt...',
            text: 'Please wait',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          const receiptBlob = await bookingApi.getBookingReceipt(firstBookingId);
          const bookingRef = `BK${firstBookingId.toString().padStart(6, '0')}`;
          let url;
          
          if (receiptBlob.type === 'text/html' || receiptBlob.type === 'text/html; charset=utf-8') {
            // Read HTML content and update title tag
            const htmlText = await receiptBlob.text();
            let updatedHtml = htmlText.replace(
              /<title>.*?<\/title>/i,
              `<title>Booking Receipt_${bookingRef}</title>`
            );
            // If no title tag exists, add one in the head section
            if (!updatedHtml.includes('<title>')) {
              updatedHtml = updatedHtml.replace(
                /<head>/i,
                `<head><title>Booking Receipt_${bookingRef}</title>`
              );
            }
            const updatedBlob = new Blob([updatedHtml], { type: 'text/html' });
            url = window.URL.createObjectURL(updatedBlob);
            
            // Open in new window and trigger print to PDF
            const newWindow = window.open(url, '_blank');
            if (newWindow) {
              newWindow.onload = () => {
                setTimeout(() => {
                  newWindow.print();
                }, 500);
              };
            } else {
              // Fallback: create download link
              const a = document.createElement('a');
              a.href = url;
              a.download = `Booking Receipt_${bookingRef}.html`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }
          } else {
            // Direct PDF download
            url = window.URL.createObjectURL(receiptBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Booking Receipt_${bookingRef}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }
          
          Swal.close();
          Swal.fire({
            icon: 'success',
            title: 'Receipt Downloaded',
            text: 'Your receipt has been downloaded successfully.',
            confirmButtonColor: '#2563eb',
            confirmButtonText: 'View My Bookings'
          }).then(() => {
            navigate('/bookings');
          });
        } catch (receiptError) {
          console.error('Error downloading receipt:', receiptError);
          Swal.close();
          Swal.fire({
            icon: 'warning',
            title: 'Receipt Download Failed',
            text: 'Booking was successful, but receipt download failed. You can download it later from your bookings.',
            confirmButtonColor: '#2563eb',
            confirmButtonText: 'View My Bookings'
          }).then(() => {
            navigate('/bookings');
          });
        }
      } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
        // User clicked "View My Bookings"
        navigate('/bookings');
      } else {
        // User closed the dialog - redirect to bookings
        navigate('/bookings');
      }

    } catch (error) {
      console.error('Error completing booking:', error);
      setPaymentStatus('error');
      setProcessing(false);
      
      Swal.fire({
        icon: 'error',
        title: 'Booking Failed',
        text: error.message || 'An error occurred while processing your booking. Please try again.',
        confirmButtonColor: '#dc2626'
      }).then(() => {
        navigate('/book');
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
        {processing && (
          <>
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-600">Please wait while we process your payment...</p>
          </>
        )}

        {paymentStatus === 'success' && !processing && (
          <>
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">Your payment has been processed successfully.</p>
            {paymentResult && (
              <div className="text-left bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm"><strong>Order ID:</strong> {paymentResult.orderId}</p>
                {paymentResult.transactionId && (
                  <p className="text-sm"><strong>Transaction ID:</strong> {paymentResult.transactionId}</p>
                )}
              </div>
            )}
            <p className="text-sm text-gray-500">Redirecting to your bookings...</p>
          </>
        )}

        {paymentStatus === 'error' && !processing && (
          <>
            <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-4">
              {paymentResult?.message || 'Payment could not be processed. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/book')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Booking
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;

